import React, { useCallback, useState } from 'react';
import { styled } from '@this/src/components/constants/themes';
import type JalBookingInventoryItem from '@this/src/domain/arrangement/jal_booking_inventory_item';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow
} from '@this/src/components/shared/ui/data_displays/table';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import Notification from '@this/src/notification';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox';
import { Fetcher } from '@this/src/util';
import { Box } from '@material-ui/core';
import JalBookingInventoryItemTableRow from './items_result_table_row';

interface Props {
  jalBookingInventoryItems: JalBookingInventoryItem[];
  totalCount: number;
  totalPage: number;
  page: number;
  fetchTodos: () => void;
  setPage: (page: number) => void;
  onCsvDownload: () => void;
}

const ItemsResultTable: React.FC<Props> = ({
  jalBookingInventoryItems,
  totalCount,
  totalPage,
  page,
  fetchTodos,
  setPage,
  onCsvDownload
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedPairIds, setSelectedPairIds] = useState<number[]>([]);
  const bulkTargetTodos = jalBookingInventoryItems.filter(
    item =>
      item.jalBookingInventoryPairId !== null &&
      item.jalBookingInventoryPairAiOperatorReservationId !== null &&
      item.jalBookingInventoryPairJalReservationId !== null &&
      item.jalBookingInventoryPairInventoriedAt === null
  );

  const handleToggleAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedPairIds(bulkTargetTodos.map(item => item.jalBookingInventoryPairId!).filter(Boolean));
      } else {
        setSelectedPairIds([]);
      }
    },
    [bulkTargetTodos]
  );

  const handleToggle = useCallback((checked: boolean, pairId: number | null) => {
    if (!pairId) return;
    setSelectedPairIds(prev => (checked ? [...prev, pairId] : prev.filter(id => id !== pairId)));
  }, []);

  const handleBulkInventory = useCallback(
    async (jalBookingInventoryPairIds: number[]) => {
      if (loading) return;

      try {
        setLoading(true);
        const params = { jal_booking_inventory_pair_ids: jalBookingInventoryPairIds };
        await Fetcher.put('/arrangement/auto_reservation_inventory/jal_booking/items/bulk_inventory', params);
        fetchTodos();
        Notification.success('一括棚卸を実行しました');
      } catch {
        Notification.error('一括棚卸に失敗しました');
      } finally {
        setLoading(false);
      }
    },
    [loading, fetchTodos]
  );

  return (
    <>
      <Flex>
        <div>検索結果:{totalCount}件</div>
        <Button
          onClick={() => handleBulkInventory(selectedPairIds)}
          disabled={totalCount === 0 || loading || selectedPairIds.length === 0}
          loading={loading}
        >
          一括棚卸
        </Button>
        <Button onClick={onCsvDownload} disabled={totalCount === 0 || loading} loading={loading}>
          CSVダウンロード
        </Button>
      </Flex>
      <Box margin="10px 0">
        ※上下2段に分かれているカラムは、上段が予約指示（ai_operator_reservation）、下段が予約（jal_reservation）のもの
      </Box>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>
              <Checkbox
                checked={
                  selectedPairIds.length > 0 &&
                  bulkTargetTodos.every(item => selectedPairIds.includes(item.jalBookingInventoryPairId!))
                }
                onChange={e => handleToggleAll(e.target.checked)}
              />
            </TableCell>
            <TableCell>ID</TableCell>
            <TableCell>作成日時</TableCell>
            <TableCell>予約指示ステータス</TableCell>
            <TableCell>予約番号</TableCell>
            <TableCell>搭乗日</TableCell>
            <TableCell>便名</TableCell>
            <TableCell>金額</TableCell>
            <TableCell>予約日</TableCell>
            <TableCell>完全自動</TableCell>
            <TableCell>棚卸ステータス</TableCell>
            <TableCell>棚卸実施者</TableCell>
            <TableCell>棚卸日時</TableCell>
            <TableCell>Trip ID</TableCell>
            <TableCell>OrderItem ID</TableCell>
            <TableCell>Trace ID</TableCell>
            <TableCell>コメント</TableCell>
            <TableCell>アクション</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jalBookingInventoryItems.map((item, i) => (
            <JalBookingInventoryItemTableRow
              key={item.jalBookingInventoryPairId}
              item={item}
              checked={selectedPairIds.includes(item.jalBookingInventoryPairId!)}
              onInventory={() => fetchTodos()}
              onToggle={(checked: boolean) => handleToggle(checked, item.jalBookingInventoryPairId)}
            />
          ))}
        </TableBody>
      </Table>
      <TablePagination
        count={totalPage}
        page={page}
        onChange={(_e: unknown, newPage: number) => setPage(newPage)}
      />
    </>
  );
};

const Flex = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export default ItemsResultTable;
